import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  IconButton,
  Modal,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';

const Facets = ({ facets, selectedFacets, onFacetChange, fetchMoreFacets }) => {
  const [expandedFacets, setExpandedFacets] = useState(
    Object.keys(facets).reduce((acc, key) => ({ ...acc, [key]: true }), {}) // Default to expanded
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentFacet, setCurrentFacet] = useState(null);
  const [modalFacets, setModalFacets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState({});

  const toggleFacetExpansion = (facetName) => {
    setExpandedFacets((prev) => ({
      ...prev,
      [facetName]: !prev[facetName],
    }));
  };

  const handleChange = (facetName, value, query) => {
    if (!query) {
      query=''
    }

    const newSelectedFilters = { ...selectedFilters, [facetName]: value };
    setSelectedFilters(newSelectedFilters); // Update filters first
  
    // Construct the URL with updated filters
    const searchParams = new URLSearchParams();
    searchParams.set('query', query);
  
    Object.keys(newSelectedFilters).forEach(key => {
      if (newSelectedFilters[key]) {
        // Use 'itemtype' for Type filter, and lowercase for other filters
        const filterKey = key === 'Publication Year' ? 'publicationyear' : 'publication_year';
        searchParams.delete(`f.${filterKey}`);
        searchParams.append(`f.${filterKey}`, `${newSelectedFilters[key]},`);
      }
    });
  
    // Update the URL with query parameters
    navigate(`/journal_articles?${searchParams.toString()}`);


  };  
    


  const handleShowMore = async (facetName) => {
    setCurrentFacet(facetName);
    setCurrentPage(0);

    try {
      const initialFacets = await fetchMoreFacets(facetName, 0, searchQuery);
      setModalFacets(initialFacets);
    } catch (error) {
      console.error('Error fetching initial facets for modal:', error);
    }

    setIsModalOpen(true);
  };

  const handleLoadMore = async () => {
    try {
      const nextPage = currentPage + 1;
      const newFacets = await fetchMoreFacets(currentFacet, nextPage, searchQuery);

      setModalFacets((prev) => [...prev, ...newFacets]);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error('Error loading more facets:', error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const facetDisplayNames = {
    publicationYear: 'Publication Year',
    topics: 'Subject Areas',
    type: 'Document Types',
    institutions: 'Affiliate Institutions',
    openAccess: 'OA Status',
    source: 'Sources',
    author: 'Authors',
  };

  return (
    <Box>
      {Object.keys(facets).map((facetName) => {
        const displayName = facetDisplayNames[facetName] || facetName;
        const facetValues = Object.entries(facets[facetName]);
        const visibleValues = facetValues.slice(0, 8); // Show only top 8 values
        const isExpanded = expandedFacets[facetName]; // Check if the facet is expanded
        const hasMoreFacets = facetValues.length > 8;

        return (
          <Box
            key={facetName}
            sx={{
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #ddd',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                {displayName}
              </Typography>
              <IconButton onClick={() => toggleFacetExpansion(facetName)} aria-label="toggle">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Divider sx={{ marginBottom: '12px' }} />


              {visibleValues.map(([value, facet]) => (
                <React.Fragment key={value}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                    }}
                  >
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          checked={selectedFacets[facetName]?.includes(value) || false}
                          onChange={() => handleChange(facetName, value)}
                          sx={{
                            color: '#007bff',
                            '&.Mui-checked': { color: '#007bff' },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#333',
                            flexGrow: 1,
                            '&:hover': { color: '#007bff', cursor: 'pointer' },
                          }}
                        >
                          {facet.key_display_name}  {/* Access the correct display name */}
                        </Typography>
                      }
                      sx={{
                        flexGrow: 1,
                        margin: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.875rem', color: '#888' }}>
                      {facet.count.toLocaleString()} {/* Access the count property */}
                    </Typography>
                  </Box>
                  <Divider />
                </React.Fragment>
              ))}


          </Box>
        );
      })}

 
    </Box>
  );
};

Facets.propTypes = {
  facets: PropTypes.object.isRequired,
  selectedFacets: PropTypes.object.isRequired,
  onFacetChange: PropTypes.func.isRequired,
  fetchMoreFacets: PropTypes.func.isRequired,
};

export default Facets;
